import type { TourStepId } from './resolve-step.js';

/**
 * How each step of the spotlight tour presents itself.
 *
 * Two kinds, because two kinds of moment happen:
 *
 *  - `spotlight` — "tap this". The screen dims and one real control is cut out of the dark with a
 *    tooltip beside it. Only that control answers, which is the point: there is one thing to do.
 *  - `hint` — "fill this in". A form cannot be filled through a hole in a dark overlay, so the
 *    page stays fully usable, a card floats over it saying what to do, and the Save button
 *    pulses. When the save lands, the server's tick moves the tour on.
 *
 * Targets are `data-tour` attributes on the real controls, so a restyle that moves a button
 * does not break the tour — only removing the attribute would.
 */

export type TourStepKind = 'spotlight' | 'hint';

export interface TourStepDef {
  kind: TourStepKind;
  /** The `data-tour` value of the control to light up. Null centres the tooltip on the screen. */
  target: string | null;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** The hint is drawn inside the open `<dialog>`, since nothing outside one is visible. */
  inDialog?: boolean;
}

export const TOUR_STEPS: Record<TourStepId, TourStepDef> = {
  // Below, not beside: driver.js places a right-hand popover from the viewport's right edge,
  // which lands a scrollbar's width short and on top of the rail item it points at. On the
  // phone's bottom bar it flips above by itself.
  'nav-customers': { kind: 'spotlight', target: 'nav-customers', side: 'bottom', align: 'start' },
  'customer-add': { kind: 'spotlight', target: 'customer-add', side: 'bottom', align: 'start' },
  'customer-form': { kind: 'hint', target: 'customer-save' },
  // A hint, not a spotlight: the rows must stay clickable, and which row is the shop's call.
  'customer-open': { kind: 'hint', target: null },
  'customer-tab-measurements': {
    kind: 'spotlight',
    target: 'customer-tab-measurements',
    side: 'bottom',
    align: 'start',
  },
  'measure-add': { kind: 'spotlight', target: 'measure-add', side: 'bottom', align: 'end' },
  'measure-save': { kind: 'hint', target: 'measure-save', inDialog: true },
  'order-entry': { kind: 'spotlight', target: null },
  'order-form': { kind: 'hint', target: 'order-save' },
};

/** The order the steps are walked in, for "Step 3 of 8". */
export const TOUR_ORDER: readonly TourStepId[] = [
  'nav-customers',
  'customer-add',
  'customer-form',
  'customer-tab-measurements',
  'measure-add',
  'measure-save',
  'order-entry',
  'order-form',
];

/**
 * "Step N of 8" for a step. The detour back to the customer list is not a step of its own in
 * the count; it stands where opening the measurements does.
 */
export function tourProgress(step: TourStepId): { current: number; total: number } {
  const counted = step === 'customer-open' ? 'customer-tab-measurements' : step;
  return { current: TOUR_ORDER.indexOf(counted) + 1, total: TOUR_ORDER.length };
}

/** The on-screen copy of a `data-tour` target: the first one that is actually laid out. */
export function findTourTarget(target: string): HTMLElement | null {
  const candidates = document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`);
  // The sidebar and the phone bar both carry the Customers link; only one is ever displayed.
  for (const candidate of candidates) {
    if (candidate.getClientRects().length > 0) return candidate;
  }
  return null;
}
